<i18n>
{
  "fr": {
    "configurator_preview": {
      "loading": "Chargement en cours…",
      "toggleModal": "Voir l'image générée"
    }
  }
}
</i18n>

<template>
  <div
    ref="container"
    :class="{ 'configurator-preview--headless': headless }"
    class="configurator-preview"
  >
    <PortalTarget name="portal-container--configurator-preview" multiple />

    <Configurator3dView
      v-if="!isContentLayoutTriggerVisible"
      ref="canvas"
      :style="canvasDimensions"
      class="configurator-preview__3d-view"
      :gltf="activeGltfUrl"
      :texture="texture"
      :camera-position="cameraPosition"
      :auto-rotate="isAutoRotateEnabled"
      :theta-angle="rotationAngle"
      @scene-ready="onSceneReady"
      @gltf-ready="onGltfReady"
      @texture-ready="onTextureReady"
    />

    <ConfiguratorTextureCanvasEnhancer
      ref="textureCanvas"
      class="configurator-preview__texture-canvas"
      headless
      full-texture
      include-background
      with-placeholders
      :resolution="Resolution.HIGH_DPI"
      @change="updateTexture"
    />

    <div
      class="configurator-preview__toolbar configurator-preview__toolbar--bottom-left"
    >
      <ZendeskButton class="configurator-preview__toolbar-item" />

      <BaseBtn
        v-if="isDebugTexture"
        class="configurator-preview__toggle-btn configurator-preview__toolbar-item"
        @click="toggleTexturePreviewModal"
      >
        {{ $t('configurator_preview.toggleModal') }}

        <ConfiguratorTexturePreviewModal
          v-model="isTexturePreviewModalActive"
        />
      </BaseBtn>
    </div>

    <transition appear name="configurator-preview__layer-loading-">
      <div
        v-if="isLoading"
        class="configurator-preview__layer configurator-preview__layer--loading configurator-preview__layer-loading"
      >
        <div class="configurator-preview__layer-loading-container">
          <LgfSpinner class="configurator-preview__spinner" />

          <p>
            {{ $t('configurator_preview.loading') }}
          </p>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import Configurator3dView from './Configurator3dView';
import LgfSpinner from '../LgfSpinner';
import { fallbackToPx } from '../../utils/CssUtils';
import ConfiguratorTextureCanvasEnhancer from './ConfiguratorTextureCanvasEnhancer';
import BaseBtn from '../BaseBtn';
import ConfiguratorTexturePreviewModal from './ConfiguratorTexturePreviewModal';
import ZendeskButton from './ZendeskButton';
import {
  ConfiguratorAction,
  ConfiguratorGetters,
  mapGetters as mapConfiguratorGetters,
  prefixType as prefixConfiguratorType,
} from '../../store/modules/configurator';
import { CameraPosition } from '../../api/static/sizes';
import { getPerimeter, Resolution } from '../../utils/MathUtils';
import { mapState as mapMiscState } from '../../store/modules/configurator/misc';

export default {
  name: 'ConfiguratorPreview',
  components: {
    ConfiguratorTexturePreviewModal,
    BaseBtn,
    ConfiguratorTextureCanvasEnhancer,
    LgfSpinner,
    Configurator3dView,
    ZendeskButton,
  },
  props: {
    width: {
      type: [Number, String],
      default: undefined,
    },
    height: {
      type: [Number, String],
      default: undefined,
    },
    headless: { type: Boolean },
    isContentLayoutTriggerVisible: { type: Boolean },
  },
  data() {
    return {
      resizeObserver: null,
      resizeObserverActive: false,
      canvasWidth: null,
      canvasHeight: null,
      sceneReady: false,
      gltfReady: false,
      textureReady: false,
      texture: null,
      isTexturePreviewModalActive: false,
      isAutoRotateEnabled: false,
      Resolution,
    };
  },
  computed: {
    ...mapConfiguratorGetters({
      activeSize: ConfiguratorGetters.GET_ACTIVE_SIZE,
      focusedZone: ConfiguratorGetters.GET_FOCUSED_ZONE,
      isGraduationVisible: ConfiguratorGetters.IS_GRADUATION_VISIBLE,
      graduationPosition: ConfiguratorGetters.GET_GRADUATION_POSITION,
      shouldUpdateCameraPosition:
        ConfiguratorGetters.SHOULD_CENTER_CAMERA_ON_GRADUATION,
      activePrintingArea: ConfiguratorGetters.GET_ACTIVE_PRINTING_AREA,
    }),
    ...mapMiscState({ isDebugTexture: 'debugTexture' }),
    /**
     *
     * @return {{width: string, height: string}}
     */
    canvasDimensions() {
      return {
        width: fallbackToPx(this.width),
        height: fallbackToPx(this.height),
      };
    },
    isLoading() {
      return !this.sceneReady || !this.gltfReady || !this.textureReady;
    },
    activeGltfUrl() {
      const activeSizeId = this.$store.state.configurator.activeSizeId;
      const sizeMap = this.$store.state.configurator.glasses.sizeMap;

      return (
        (activeSizeId &&
          sizeMap &&
          sizeMap[activeSizeId] &&
          sizeMap[activeSizeId].gltfUrl) ||
        null
      );
    },
    cameraPosition() {
      return (
        this.activeSize &&
        this.activeSize.cameraPosition &&
        this.activeSize.cameraPosition[CameraPosition.CENTER]
      );
    },
    rotationAngle() {
      /** @type {Zone} */
      const focusedZone = this.focusedZone;
      /** @type {Boolean} */
      const shouldUpdateCameraPosition = this.shouldUpdateCameraPosition;

      if (!focusedZone && !shouldUpdateCameraPosition) {
        return undefined;
      }

      const textureWidth = getPerimeter(this.activeSize.diameter);
      const printingAreaX = (textureWidth - this.activePrintingArea.width) / 2;

      const graduationPosition =
        (textureWidth - 19) * (this.graduationPosition / 100);

      const xValue = focusedZone
        ? printingAreaX + focusedZone.x + focusedZone.width / 2
        : graduationPosition + 19 / 2;

      if (shouldUpdateCameraPosition) {
        this.$store.dispatch(
          prefixConfiguratorType(ConfiguratorAction.SET_ACTIVE_STATE),
          { shouldCenterCameraOnGraduation: false, fallbackWhenNotFound: true }
        );
      }

      // Converting xValue between 0 and textureWidth to a radian angle between -Math.PI and Math.Pi
      return (xValue * (2 * Math.PI)) / textureWidth - Math.PI;
    },
  },
  watch: {
    headless: {
      handler(headless) {
        if (headless && (!this.width || !this.height)) {
          throw new Error(
            'Width and height props are mandatory in headless mode'
          );
        }
      },
      immediate: true,
    },
    activeGltfUrl() {
      this.gltfReady = false;
    },
    texture() {
      this.textureReady = false;
    },
  },
  methods: {
    onSceneReady() {
      this.sceneReady = true;
    },
    onGltfReady() {
      this.gltfReady = true;
    },
    onTextureReady() {
      this.textureReady = true;
    },
    // Expose canvas export API to parent component
    ...['toBase64', 'toBlob'].reduce((acc, method) => {
      acc[method] = function () {
        return this.$refs.canvas[method]();
      };

      return acc;
    }, {}),
    async updateTexture() {
      this.texture = await this.$refs.textureCanvas.toBase64();
    },
    toggleTexturePreviewModal() {
      this.isTexturePreviewModalActive = !this.isTexturePreviewModalActive;
    },
  },
};
</script>

<style lang="scss" scoped>
.configurator-preview {
  position: relative;

  &--headless {
    @include sr-only();
  }

  &__layer {
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    width: 100%;
    height: 100%;

    &--loading {
      display: flex;
      z-index: 1;
    }
  }

  &__3d-view {
    width: 100%;
    height: 100%;
  }

  &__layer-loading {
    &--enter,
    &--leave-to {
      opacity: 0;
    }

    &--enter-active,
    &--leave-active {
      transition: opacity $default-transition-duration
        $default-transition-duration;
    }
  }

  &__layer-loading-container {
    margin: auto;
    text-align: center;
  }

  &__spinner {
    $font-size: 4em;
    font-size: $font-size;
    margin-bottom: 1em / parse-int($font-size);
  }

  &__preview-btn {
    $left: 70%;
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    left: $left;
    font-family: inherit;
    border: 0;
    font-weight: inherit;
    background: none;
    padding: 0;
    max-width: string.unquote(
      'calc(#{100% - $left} - #{$lateral-tool-width + 0.25em})'
    );
    text-align: left;
    text-decoration: underline;
    color: $color-secondary;
    cursor: pointer;

    &:focus {
      outline: none;
    }

    &::after {
      @extend %icon;
      @extend %icon--expand;
      margin-top: 0.25em;
      font-size: 1.25em;
      display: block;
    }
  }

  &__toolbar {
    position: absolute;

    &--bottom-left {
      display: none;

      @include breakpoint($breakpoint-configurator-l) {
        display: block;
        bottom: 0;
        left: 1em;
      }
    }

    &--bottom-right {
      display: block;
      bottom: 0;
      right: 1em;

      @include breakpoint(1325px) {
        display: none;
      }
    }
  }

  &__toolbar-item {
    display: block;
    margin-bottom: 1em;

    &:last-child {
      margin-bottom: 0;
    }
  }
}
</style>
